import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { DOCK_CATEGORIES_TYPES } from 'components/farm-dashboard/components/WidgetDock/types/dockTypes'
import { DEFAULT_WIDGETS } from 'components/farm-dashboard/constants/defaultWidgets'
import {
	WidgetConfig,
	WidgetRow,
	WidgetType,
} from 'components/farm-dashboard/types'
import { removeEmptyRows } from 'components/farm-dashboard/utils/widgetRowUtils'
import axAPI from 'httpConfig'
import { IAuthPermissions, LicensesType } from 'models/IUserData'

export const fetchWidgetSettings = createAsyncThunk(
	'dashboard/fetchWidgetSettings',
	async (_, { rejectWithValue }) => {
		try {
			const response = await axAPI.get('auth/users/me/widgets/settings')
			return response.data
		} catch (error) {
			return rejectWithValue(error)
		}
	},
)

export const saveWidgetSettings = createAsyncThunk(
	'dashboard/saveWidgetSettings',
	async (rows: WidgetRow[], { rejectWithValue }) => {
		try {
			await axAPI.patch('auth/users/me/widgets/settings', {
				setting_json: { rows },
			})
		} catch (error) {
			return rejectWithValue(error)
		}
	},
)

// Функция фильтрации виджетов по лицензиям
export const filterWidgetsByLicense = (
	widgets: WidgetConfig[],
	permissions: IAuthPermissions | null | undefined,
): WidgetConfig[] => {
	if (!permissions) return []
	return widgets.filter(widget => {
		if (!widget.license) return true // Если лицензия не указана - показываем виджет

		// Проверяем наличие соответствующей лицензии в permissions
		switch (widget.license) {
			case LicensesType.IDENTIFICATION:
				return permissions.license_identification
			case LicensesType.FOOD_TABLE:
				return permissions.license_food_table
			case LicensesType.LAMENESS:
				return permissions.license_limpness
			case LicensesType.FATNESS:
				return permissions.license_fatness
			case LicensesType.MILKING_PROCESS:
				return permissions.license_milking_process_check
			default:
				return true
		}
	})
}

export interface DashboardState {
	isEditMode: boolean
	isWidgetDockVisible: boolean
	isWidgetDockExpanded: boolean
	selectedCategory: DOCK_CATEGORIES_TYPES
	widgets: WidgetConfig[] // Для обратной совместимости
	savedWidgets: WidgetConfig[] // Для обратной совместимости
	rows: WidgetRow[] // Новая структура со строками
	savedRows: WidgetRow[] // Сохраненные строки
	isSettingsLoading: boolean
}
const defaultState: DashboardState = {
	isEditMode: false,
	isWidgetDockVisible: false,
	isWidgetDockExpanded: false,
	selectedCategory: DOCK_CATEGORIES_TYPES.ALL,
	widgets: DEFAULT_WIDGETS,
	savedWidgets: DEFAULT_WIDGETS,
	rows: [],
	savedRows: [],
	isSettingsLoading: true,
}
const dashboardSlice = createSlice({
	name: 'dashboard',
	initialState: defaultState,
	reducers: {
		toggleDashboardEditMode: state => {
			if (!state.isEditMode) {
				state.savedRows = JSON.parse(JSON.stringify(state.rows))
				state.savedWidgets = JSON.parse(JSON.stringify(state.widgets))
				state.isWidgetDockVisible = true
				state.isWidgetDockExpanded = false
			} else {
				state.isWidgetDockVisible = false
				state.isWidgetDockExpanded = false
			}
			state.isEditMode = !state.isEditMode
		},
		reorderWidgets: (state, action: PayloadAction<WidgetConfig[]>) => {
			state.widgets = action.payload
		},
		toggleWidgetDock: state => {
			state.isWidgetDockExpanded = !state.isWidgetDockExpanded
		},
		openWidgetDock: state => {
			state.isWidgetDockExpanded = true
		},
		closeWidgetDock: state => {
			state.isWidgetDockExpanded = false
		},
		deleteDashboardWidget: (state, action: PayloadAction<WidgetType>) => {
			state.widgets = state.widgets.filter(
				widget => widget.type !== action.payload,
			)
		},
		cancelDashboardEdit: state => {
			state.rows = JSON.parse(JSON.stringify(state.savedRows))
			state.widgets = JSON.parse(JSON.stringify(state.savedWidgets))
			state.isWidgetDockVisible = false
			state.isWidgetDockExpanded = false
			state.isEditMode = false
		},
		saveDashboardWidgets: state => {
			state.savedWidgets = state.widgets
			state.savedRows = state.rows
		},
		setSelectedCategory: (
			state,
			action: PayloadAction<DOCK_CATEGORIES_TYPES>,
		) => {
			state.selectedCategory = action.payload
		},
		initializeWidgetsWithLicenses: (
			state,
			action: PayloadAction<IAuthPermissions>,
		) => {
			const filteredWidgets = filterWidgetsByLicense(
				DEFAULT_WIDGETS,
				action.payload,
			)
			state.widgets = filteredWidgets
			state.savedWidgets = filteredWidgets
		},
		setDashboardRows: (state, action: PayloadAction<WidgetRow[]>) => {
			state.rows = action.payload
		},
		updateDashboardRows: (state, action: PayloadAction<WidgetRow[]>) => {
			state.rows = action.payload
		},
		moveWidgetBetweenRows: (
			state,
			action: PayloadAction<{
				fromRowId: string
				toRowId: string
				widgetId: string
			}>,
		) => {
			const { fromRowId, toRowId, widgetId } = action.payload
			const fromRowIndex = state.rows.findIndex(r => r.id === fromRowId)
			const toRowIndex = state.rows.findIndex(r => r.id === toRowId)

			if (fromRowIndex === -1 || toRowIndex === -1) return

			const widget = state.rows[fromRowIndex].widgets.find(
				w => w.id === widgetId,
			)
			if (!widget) return

			// Удаляем виджет из исходной строки
			state.rows[fromRowIndex].widgets = state.rows[
				fromRowIndex
			].widgets.filter(w => w.id !== widgetId)

			// Добавляем виджет в целевую строку
			state.rows[toRowIndex].widgets.push(widget)
		},
		reorderWidgetsInRow: (
			state,
			action: PayloadAction<{
				rowId: string
				widgets: WidgetConfig[]
			}>,
		) => {
			const { rowId, widgets } = action.payload
			const rowIndex = state.rows.findIndex(r => r.id === rowId)
			if (rowIndex !== -1) {
				state.rows[rowIndex].widgets = widgets
			}
		},
		deleteWidgetFromRow: (
			state,
			action: PayloadAction<{
				rowId: string
				widgetType: WidgetType
			}>,
		) => {
			const { rowId, widgetType } = action.payload
			const rowIndex = state.rows.findIndex(r => r.id === rowId)
			if (rowIndex !== -1) {
				state.rows[rowIndex].widgets = state.rows[rowIndex].widgets.filter(
					w => w.type !== widgetType,
				)
			}
			// Удаляем пустые строки
			state.rows = removeEmptyRows(state.rows)
			// Также обновляем старую структуру для совместимости
			state.widgets = state.widgets.filter(w => w.type !== widgetType)
		},
		saveDashboardRows: state => {
			state.savedRows = state.rows
		},
		cancelDashboardRows: state => {
			state.rows = state.savedRows
		},
	},
	extraReducers: builder => {
		builder
			.addCase(fetchWidgetSettings.pending, state => {
				state.isSettingsLoading = true
			})
			.addCase(fetchWidgetSettings.fulfilled, (state, action) => {
				state.isSettingsLoading = false
				const rows = action.payload?.setting_json?.rows
				if (Array.isArray(rows) && rows.length > 0) {
					state.rows = rows
					state.savedRows = rows
				}
			})
			.addCase(fetchWidgetSettings.rejected, state => {
				state.isSettingsLoading = false
			})
	},
})

export default dashboardSlice.reducer
export const {
	toggleDashboardEditMode,
	reorderWidgets,
	deleteDashboardWidget,
	cancelDashboardEdit,
	saveDashboardWidgets,
	toggleWidgetDock,
	openWidgetDock,
	closeWidgetDock,
	setSelectedCategory,
	initializeWidgetsWithLicenses,
	setDashboardRows,
	updateDashboardRows,
	moveWidgetBetweenRows,
	reorderWidgetsInRow,
	deleteWidgetFromRow,
	saveDashboardRows,
	cancelDashboardRows,
} = dashboardSlice.actions
